import { ReactElement } from 'react';

import { generateBorderId, generateFillId, generateSVGIcon, generateSvgId } from './Shapes.helpers';
import { ResizeShape, ShapePathProps, ShapeProps } from './Shapes.types';

const generatePentagonFillPath = ({ widthPx, heightPx, borderWidth }: ShapePathProps) => {
  const theta = Math.atan(heightPx / 3 / (widthPx / 2));
  const gamma = Math.atan((2 * heightPx) / 3 / (widthPx / 5));
  return `
    M ${widthPx / 2}, ${borderWidth / Math.cos(theta)}
    L ${widthPx - (borderWidth / Math.sin((theta + gamma) / 2)) * Math.cos((gamma - theta) / 2)}, ${
    (widthPx / 2) * Math.tan(theta) + (borderWidth / Math.sin((theta + gamma) / 2)) * Math.sin((gamma - theta) / 2)
  }
    L ${widthPx - borderWidth / Math.tan((theta + gamma) / 2) - widthPx / 5}, ${heightPx - borderWidth}
    L ${borderWidth / Math.tan((theta + gamma) / 2) + widthPx / 5}, ${heightPx - borderWidth}
    L ${(borderWidth / Math.sin((theta + gamma) / 2)) * Math.cos((gamma - theta) / 2)}, ${
    (widthPx / 2) * Math.tan(theta) + (borderWidth / Math.sin((theta + gamma) / 2)) * Math.sin((gamma - theta) / 2)
  }
    Z
  `;
};

const generatePentagonBorderPath = ({ widthPx, heightPx, borderWidth }: ShapePathProps) => {
  const theta = Math.atan(heightPx / 3 / (widthPx / 2));
  const gamma = Math.atan((2 * heightPx) / 3 / (widthPx / 5));
  return `
    M ${widthPx / 2}, ${borderWidth / 2 / Math.cos(theta)}
    L ${widthPx - (borderWidth / 2 / Math.sin((theta + gamma) / 2)) * Math.cos((gamma - theta) / 2)}, ${
    (widthPx / 2) * Math.tan(theta) + (borderWidth / 2 / Math.sin((theta + gamma) / 2)) * Math.sin((gamma - theta) / 2)
  }
    L ${widthPx - borderWidth / 2 / Math.tan((theta + gamma) / 2) - widthPx / 5}, ${heightPx - borderWidth / 2}
    L ${borderWidth / 2 / Math.tan((theta + gamma) / 2) + widthPx / 5}, ${heightPx - borderWidth / 2}
    L ${(borderWidth / 2 / Math.sin((theta + gamma) / 2)) * Math.cos((gamma - theta) / 2)}, ${
    (widthPx / 2) * Math.tan(theta) + (borderWidth / 2 / Math.sin((theta + gamma) / 2)) * Math.sin((gamma - theta) / 2)
  }
    Z
  `;
};

export const resizePentagon = ({ widthPx, heightPx, borderWidth, id }: ResizeShape) => {
  const svg = document.getElementById(generateSvgId(id));
  const svgFill = document.getElementById(generateFillId(id));
  const svgBorder = document.getElementById(generateBorderId(id));

  if (svg && svgFill && svgBorder) {
    // set svg dimensions
    svg.setAttribute('width', `${widthPx}px`);
    svg.setAttribute('height', `${heightPx}px`);

    // set fill dimensions
    svgFill.setAttribute('d', generatePentagonFillPath({ widthPx, heightPx, borderWidth }));
    // set border dimensions
    svgBorder.setAttribute('d', generatePentagonBorderPath({ widthPx, heightPx, borderWidth }));
  }
};

const Pentagon = ({ widthPx, heightPx, border, id, fillColor, fillPercent, ...other }: ShapeProps): ReactElement => {
  const { width: borderWidth } = border;
  const iconProps = {
    widthPx,
    heightPx,
    border,
    id,
    fillColor,
    fillPercent,
    fillPath: generatePentagonFillPath({ widthPx, heightPx, borderWidth }),
    borderPath: generatePentagonBorderPath({ widthPx, heightPx, borderWidth }),
    ...other,
  };

  return generateSVGIcon(iconProps);
};

export default Pentagon;
